<script setup lang="ts">
/**
 * @author Alexander Kenchoshvili, Luka Tchigladze
 */

import { watch, defineAsyncComponent, reactive, computed, onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";
import { reqAxios, tr } from "@/composables/core/func";
import { useMainStore } from "@/store/main/index";
import { extractFromQuery, updateQuery } from "@/composables/utils/router";
import { fetchData } from "./ts/fetchHandler";

import { getTaxonomySS } from "@/composables/core/func";
import { useMyComposable } from "@/composables/core/conf";

import type { SecondaryDataInterface } from "@/types/ContentType";
import type { SelectInterface } from "@/types/FormInterface";

import NewsCard from "./parts/NewsCard.vue";
import Skeletons from "./parts/Skeleton.vue";
import SearchInput from "@/components/regularComponents/UIComponents/SearchInput.vue";
import FilterSelect from "@/components/regularComponents/FormComponents/FilterSelect.vue";

const Pagination = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Pagination/Pagination.vue")
);

const props = defineProps<{
  contentData: SecondaryDataInterface | undefined;
}>();

await getTaxonomySS("media_categories");
await getTaxonomySS("year_filter");

const years = ref<SelectInterface[]>([]);
const title = props?.contentData?.conf?.header?.title;

const store = useMainStore();
const route = useRoute();

const newsData = ref<SecondaryDataInterface["data"]>();

const state = reactive({
  perPage: 12,
  category: extractFromQuery("category") || "all",
  searchText: extractFromQuery("searchText"),
  isLoading: false,
});

const { t } = useI18n();

const conf = useMyComposable();

const { data: yearData } = await useFetch<any>(conf.apiUrl + "gumbati/filterYears");

const onYearSelect = (e?: SelectInterface) => {
  if (e?.value) {
    updateQuery("year", e.value === "all" ? "" : e.value);
  }
};

watch(
  route,
  async (n) => {
    newsData.value = await fetchData(state, props.contentData);
  },
  { immediate: true }
);

onBeforeMount(async () => {
  const routeYear = extractFromQuery("year");

  const reversedYears = yearData?.value?.reverse();

  const fromApi = (reversedYears as number[])?.map((el) => {
    return {
      title: el.toString(),
      value: el.toString(),
      isActive: Number(routeYear) === el,
    };
  });

  years.value?.push({
    title: t("all"),
    value: "all",
  });

  years.value?.push(...fromApi);
});
</script>

<template>
  <div class="news-list">
    <div class="container">
      <div class="news-list__header d-flex j-btw">
        <h1 class="news-list__title hl-1-plus pos-rel uppercase">{{ title }}</h1>
        <div class="news-list__search-wrapper pos-rel">
          <SearchInput
            placeholderText="Search"
            :modelValue="state.searchText"
            @update="(value) => (state.searchText = value)"
          />
          <FilterSelect v-if="years?.length" :items="years" @select="onYearSelect" />
        </div>
      </div>

      <div class="news__cards pos-rel">
        <template v-if="state.isLoading">
          <Skeletons class="news__skeletons" v-for="i in state.perPage" />
        </template>
        <template v-if="newsData?.listCount && !state.isLoading">
          <NewsCard
            v-for="(item, index) in newsData?.list"
            :key="index"
            :data="item"
            :is-date="true"
          />
        </template>
      </div>

      <div
        class="news__pagination d-flex j-center"
        v-if="newsData?.listCount && !state.isLoading"
      >
        <Pagination
          v-if="newsData?.listCount! > state.perPage"
          :perPage="state.perPage"
          :total="newsData!.listCount"
        />
      </div>
    </div>
  </div>
</template>

<style src="./styles/NewsList.scss" />
